import { useMemo } from 'react';
import { MessageSimple, useMessageContext } from 'stream-chat-react';

import { useConversationContext } from './conversationProvider';

/**
 * Stream's default message UI, with the sender relabelled as the store.
 *
 * Our Stream users are created without a top-level `name` — the API nests
 * custom fields under `metadata`, which Stream treats as opaque — so the
 * default UI falls back to `message.user.name || message.user.id` and leaks a
 * raw user UUID into both the message metadata and the avatar. Shoppers should
 * see the store, not the staff member who happens to be replying.
 *
 * Stream exposes no component slot for the sender name alone (`Message` is the
 * only override point), and its v2 theming covers colour and typography only —
 * it cannot change what the name says. Rather than reimplement `MessageSimple`
 * and hand-copy its `str-chat__*` classes, we hand the real component a message
 * whose `user` carries the identity we want displayed. All of Stream's markup
 * and theming stays intact, and nothing here breaks when that markup changes.
 * @see https://getstream.io/chat/docs/sdk/react/v11/components/message-components/message-ui/
 */
export const CustomMessage = () => {
  const { isMyMessage, message } = useMessageContext();
  const { store, user } = useConversationContext();

  const messageWithSenderIdentity = useMemo(() => {
    if (!message.user) return message;

    // The shopper's own messages render no name, but they do render an avatar,
    // which falls back to the first character of whatever `name` resolves to.
    const senderIdentity = isMyMessage()
      ? { name: user.first_initial }
      : { image: store.photo, name: store.name };

    return { ...message, user: { ...message.user, ...senderIdentity } };
  }, [isMyMessage, message, store.name, store.photo, user.first_initial]);

  return <MessageSimple message={messageWithSenderIdentity} />;
};
